import { useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "react-styled-flexboxgrid";

import useSubmitForm from "./hooks/useSubmitForm";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import { TUseLocation } from "./types";

import Box from "../../Global/Atomics/Box"
import { StyledButton } from "../../Global/Atomics/Button";
import { Container } from "../../Global/Atomics/Container";
import { Heading } from "../../Global/Atomics/Heading";
import NovaCircle from "../../Global/Atomics/NovaCircle"
import ProgressTracker from "../../Global/Atomics/ProgressTracker";
import CustomIcon from "../../Global/Icons/CustomIcon";
import FeedbackPage from "../../Global/Widgets/Feedbacks/FeedbackPage";

import { useResponsiveContext } from "../../../contexts/ResponsiveContext";
import { useTranslationContext } from "../../../contexts/TranslationContext";
import { useGlobalStateContext } from "../../../contexts/GlobalStateContext";

const Report = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const { state: { section } } = useLocation() as TUseLocation;
    const { resources, translate } = useTranslationContext();
    const { globalState: { showList }, setGlobalState } = useGlobalStateContext();
    const [viewMode] = useResponsiveContext();
    const { mutate, isLoading, isError, isSuccess } = useSubmitForm(); // API Call to submit form

    const isMobile = viewMode === 'mobile';
    let size = isMobile ? 158 : 220;

    const memoizedSwitchStep = useCallback((step: number) => {
        setStep(step);
    }, []);

    const handleSubmit = (formData: any) => {
        //@ts-ignore
        mutate(formData);
        memoizedSwitchStep(step + 1);
    }

    const handleArrowBack = () => {
        if (showList && step === 1) {
            // If user is on step 1 and watching the incident list, when clicking the return button, it should NOT go back to homepage
            // but instead, it should set the showList variable to false so that he can see the main categories again
            setGlobalState({ id: 'showList', value: false }); 
        } else if (step > 1) {
            setStep(step - 1);
        } else {
            setGlobalState({ reset: true }); // Reset global state when returning to homepage
            navigate(-1);
        }
    };

    const handleClosePage = () => {
        setGlobalState({ reset: true }); // Reset global state when returning to homepage
        navigate(-1);
    };

    const Steps = () => {
        switch (step) {
            case 1:
                return <Step1 step={step} setStep={memoizedSwitchStep} resources={resources} translate={translate} />
            case 2:
                return <Step2 step={step} setStep={memoizedSwitchStep} resources={resources} />
            case 3:
                return <Step3 step={step} setStep={memoizedSwitchStep} handleSubmit={handleSubmit} resources={resources} />
            case 4:
                return <FeedbackPage
                    step={step}
                    isError={isError}
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                    onErrorCTA={() => memoizedSwitchStep(step - 1)}
                />

            default:
                return <></>
        }
    }

    return (
        <>
            {/* BANNER */}
            <Box position="relative" bg="black-1" height={isMobile ? 'auto' : '25vh'}>
                <Container py="lg">
                    <NovaCircle size={size} customPosition="top-right" zIndex={1} />
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <StyledButton onClick={handleArrowBack} aria-label="Navigate back"><CustomIcon icon="ArrowLeft_sm" customColor="white" /></StyledButton>
                        <StyledButton onClick={handleClosePage} aria-label="Close"><CustomIcon icon="ActionClose_sm" customColor="white" /></StyledButton>
                    </Box>
                    <Row center="md">
                        <Col xs={6} md={9}>
                            <Box my="md">
                                <Heading level={1} typography="text-l-bold" customColor="white">{resources.report.bannerTitle}</Heading>
                            </Box>
                        </Col>
                    </Row>
                    <Row center="md">
                        <Col xs={12} md={9}>
                            <Box display="flex" alignItems="center" justifyContent="center" width="fit-content" bg="white" borderRadius="xxl" py="sm" px="md" mb="sm">
                                <CustomIcon icon="NavigationPin_xs" customColor="black-1" />
                                <Box as="span" typography="ui-xs-medium" customColor="black-1" ml="xs">Secção {section}</Box>
                            </Box>
                        </Col>
                    </Row>
                </Container>
            </Box>

            {/* CONTENT */}
            <Container>
                <Row center="md">
                    <Col xs={12} md={9}>
                        <Box mt="sm" mb="lg">
                            {step <= 3 && <ProgressTracker step={step} maxStep={3} stepIsValid={true} setStep={setStep} />}
                            <Steps />
                        </Box>
                    </Col>
                </Row>
            </Container>
        </>
    )
}

export default Report